import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import special from "../styles/special.module.scss";
import { UserContext } from "../context/UserContext";
import {
  isSharedBrandSession,
} from "../utilities/loginAccount";
import registerAccount, {
  registerErrorMessage,
} from "../utilities/registerAccount";

const getPreferredBrand = (userInformation, searchParams) => {
  if (isSharedBrandSession(userInformation)) {
    return String(userInformation.vendor || "")
      .trim()
      .toLowerCase();
  }

  return String(searchParams.get("brand") || "")
    .trim()
    .toLowerCase();
};

function RegisterAccount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userInformation } = useContext(UserContext);
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [brand, setBrand] = useState(() =>
    getPreferredBrand(userInformation, searchParams),
  );
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const brandLocked = isSharedBrandSession(userInformation);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [completed]);

  useEffect(() => {
    const preferredBrand = getPreferredBrand(userInformation, searchParams);
    if (preferredBrand) {
      setBrand(preferredBrand);
    }
  }, [userInformation, searchParams]);

  const registerVisualStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(8, 12, 12, 0.12), rgba(8, 12, 12, 0.84)), url(${process.env.PUBLIC_URL}/images/contact.jpg)`,
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    const trimmedName = userName.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName) {
      setMessage("Enter a name for this account.");
      return;
    }

    if (!trimmedEmail) {
      setMessage("Enter an email address.");
      return;
    }

    if (!password) {
      setMessage("Enter a password.");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      await registerAccount({
        userName: trimmedName,
        email: trimmedEmail,
        password,
        brand,
        userType: "designer",
      });
      setCompleted(true);
    } catch (error) {
      setMessage(registerErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (completed) {
    return (
      <div className={special.contactShell}>
        <div className={special.contactPage}>
          <div className={special.contactBackdrop}></div>
          <div className={special.contactFrame}>
            <div className={special.contactVisual} style={registerVisualStyle}>
              <div className={special.contactVisualOverlay}></div>
              <div className={special.contactVisualCopy}>
                <p className={special.contactVisualEyebrow}>Registration</p>
                <h2>Account created.</h2>
                <p>
                  {email.trim()} can now sign in from the Matlib landing page.
                </p>
              </div>
            </div>

            <div className={special.contactForm}>
              <h1>Registration complete</h1>
              <p className={special.contactIntro}>
                The account was created successfully. You can sign in from the
                Matlib landing page using the email and password you chose.
              </p>
              <div className={special.registerActions}>
                <button
                  type="button"
                  className={special.contactButton}
                  onClick={() => navigate("/")}
                >
                  Go to sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={special.contactShell}>
      <div className={special.contactPage}>
        <div className={special.contactBackdrop}></div>
        <div className={special.contactFrame}>
          <div className={special.contactVisual} style={registerVisualStyle}>
            <div className={special.contactVisualOverlay}></div>
            <div className={special.contactVisualCopy}>
              <p className={special.contactVisualEyebrow}>Registration</p>
              <h2>Create a Matlib account.</h2>
              <p>
                Register a designer account with saved collections, PDF export,
                and a personal login across devices.
              </p>
            </div>
          </div>

          <form className={special.contactForm} onSubmit={handleSubmit}>
            <h1>Register</h1>
            <p className={special.contactIntro}>
              Create a designer account to save collections, export PDFs, and
              sign in with your own credentials.
            </p>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-name">Name</label>
              <input
                id="register-name"
                type="text"
                autoComplete="name"
                placeholder="whatever name you want"
                value={userName}
                onChange={(event) => setUserName(event.target.value)}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-email">Email</label>
              <input
                id="register-email"
                type="email"
                autoComplete="email"
                placeholder="you@brand.whatever..."
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-brand">Brand</label>
              <input
                id="register-brand"
                type="text"
                autoComplete="organization"
                placeholder="Brand (optional)"
                value={brand}
                readOnly={brandLocked}
                onChange={(event) => {
                  if (!brandLocked) {
                    setBrand(event.target.value);
                  }
                }}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-password">Password</label>
              <input
                id="register-password"
                type="password"
                autoComplete="new-password"
                placeholder="Create a password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-confirm-password">Confirm password</label>
              <input
                id="register-confirm-password"
                type="password"
                autoComplete="new-password"
                placeholder="Repeat password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>

            {message ? (
              <div className={special.registerErrorMessage}>{message}</div>
            ) : null}

            <div className={special.registerActions}>
              <button
                type="submit"
                className={special.contactButton}
                disabled={loading}
              >
                {loading ? "Creating account..." : "Create account"}
              </button>
              <button
                type="button"
                className={special.registerSecondaryButton}
                onClick={() => navigate("/")}
              >
                Back to sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegisterAccount;
